import { buildComponentHtml } from "../model/defaultComponents";
import { buildCellInnerStyleCss, cellGapPaddingCss } from "../model/cellStyleUtils";
import { buildLayoutInnerStyleCss } from "../model/layoutInnerStyleUtils";
import { columnVerticalAlignToCss } from "../utils/editorFieldUtils";
import { wrapLayoutBlockHtml } from "../utils/layoutStyleUtils";
import { alignContentWrapHtml } from "../utils/componentLayoutUtils";
import { WCP_CLASS, WCP_EMAIL_STYLES, WCP_PREVIEW_STYLES } from "./emailHtmlStyles";

const escapeHtml = (value) =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const mergeStyles = (...parts) => {
  const css = parts.filter(Boolean).join(" ");
  return css ? ` style="${css.replace(/"/g, "&quot;")}"` : "";
};

const renderColumnChildren = (column) =>
  (column.children || [])
    .map((child) => {
      if (child.type === "grid") {
        return renderGrid(child);
      }
      if (child.type === "component") {
        return buildComponentHtml(child);
      }
      return "";
    })
    .join("\n");

const wrapCellBody = (content, columnProps) => {
  const vAlign = columnProps?.contentVAlign;
  if (!vAlign || vAlign === "top") {
    return content;
  }
  return alignContentWrapHtml({ contentAlign: "left", contentVAlign: vAlign }, content);
};

const renderGridTable = (grid) => {
  const gridStyle = mergeStyles("width: 100%;", "max-width: 100%;", "border-collapse: collapse;");

  const rows = (grid.rows || [])
    .map((row) => {
      const columns = (row.columns || [])
        .map((column) => {
          const width = column.widthPercent || 100;
          const innerStyle = mergeStyles(buildCellInnerStyleCss(column.props));
          const rawContent =
            renderColumnChildren(column) || `<p class="${WCP_CLASS.p} ${WCP_CLASS.imgPlaceholder}">&nbsp;</p>`;
          const content = wrapCellBody(rawContent, column.props);
          const gapPadding = cellGapPaddingCss(grid.props?.cellGap);
          const valign = columnVerticalAlignToCss(column.props?.contentVAlign);
          const tdStyle = mergeStyles(gapPadding, `vertical-align: ${valign};`);
          return `<td class="${WCP_CLASS.col}" width="${width}%" valign="${valign}"${tdStyle}><div class="${WCP_CLASS.cellInner}"${innerStyle}>${content}</div></td>`;
        })
        .join("\n");
      return `<tr class="${WCP_CLASS.row}">${columns}</tr>`;
    })
    .join("\n");

  return `<table class="${WCP_CLASS.grid}" role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"${gridStyle}>\n${rows}\n</table>`;
};

const renderGrid = (grid) => {
  const table = renderGridTable(grid);
  const innerStyle = mergeStyles(buildLayoutInnerStyleCss(grid.props));
  const innerDiv = `<div class="${WCP_CLASS.layoutInner}"${innerStyle}>${table}</div>`;
  return wrapLayoutBlockHtml(innerDiv, grid.props);
};

const renderLayout = (layout) => (layout.grids || []).map((grid) => renderGrid(grid)).join("\n");

export const compileDocumentBodyHtml = (document) => {
  const layout = document.layout || document.root;
  if (layout?.grids) {
    const body = renderLayout(layout);
    return body.trim() ? body : `<p class="${WCP_CLASS.p} ${WCP_CLASS.imgPlaceholder}">&nbsp;</p>`;
  }
  if (layout?.type === "grid") {
    return renderGrid(layout);
  }
  return `<p class="${WCP_CLASS.p} ${WCP_CLASS.imgPlaceholder}">&nbsp;</p>`;
};

export const compileDocumentToHtml = (document, options = {}) => {
  const { preview = false } = options;
  try {
    const bodyContent = compileDocumentBodyHtml(document);
    const subject = escapeHtml(document.subject || document.name || "Newsletter");
    const previewStyles = preview ? `\n  <style>${WCP_PREVIEW_STYLES}</style>` : "";
    const html = `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${subject}</title>
  <style>${WCP_EMAIL_STYLES}</style>${previewStyles}
</head>
<body class="${WCP_CLASS.body}">
  <div class="${WCP_CLASS.container}">
${bodyContent}
  </div>
</body>
</html>`;
    return { html, errors: [] };
  } catch (err) {
    return { html: "", errors: [err.message || "Errore compilazione HTML"] };
  }
};

 